﻿/**
 * 单位管理 - 后端 API 真实调用实现
 * 替代 mock/organization.ts 中的 localStorage 逻辑
 */

import { httpClient, httpGet, httpPost, httpPut, httpDelete } from '@/utils/httpClient'
import { wrapResponse, type ApiResponse, type PaginatedResponse } from '@/services/types'
import type {
  Organization,
  CreateOrgDto,
  UpdateOrgDto,
  OrgListParams,
  OrgStats,
  BatchImportResult,
  OrganizationStatus,
  ConvertOrgDto,
  ConvertOrgResult,
  OrgMember,
  OrganizationSubscription,
} from '@/types/organization'

const BASE = '/api/admin/organizations'

export const organizationApi = {
  // ==================== CRUD ====================

  async listOrganizations(params: OrgListParams = {}): Promise<ApiResponse<PaginatedResponse<Organization>>> {
    const query: Record<string, string | number> = {}
    if (params.keyword) query.keyword = params.keyword
    if (params.type) query.type = params.type
    if (params.status) query.status = params.status
    if (params.region) query.region = params.region
    if (params.planType) query.planType = params.planType
    if (params.page) query.page = params.page
    if (params.pageSize) query.pageSize = params.pageSize

    const data = await httpGet<PaginatedResponse<Organization>>(BASE, { params: query })
    return wrapResponse(data)
  },

  async getOrganization(id: string): Promise<ApiResponse<Organization>> {
    const data = await httpGet<Organization>(`${BASE}/${id}`)
    return wrapResponse(data)
  },

  async createOrganization(dto: CreateOrgDto): Promise<ApiResponse<Organization>> {
    const data = await httpPost<Organization>(BASE, dto)
    return wrapResponse(data)
  },

  async updateOrganization(id: string, dto: UpdateOrgDto): Promise<ApiResponse<Organization>> {
    const data = await httpPut<Organization>(`${BASE}/${id}`, dto)
    return wrapResponse(data)
  },

  async deleteOrganization(id: string): Promise<ApiResponse<void>> {
    await httpDelete(`${BASE}/${id}`)
    return wrapResponse(undefined)
  },

  // ==================== Status ====================

  async changeOrganizationStatus(id: string, status: OrganizationStatus, reason: string): Promise<ApiResponse<Organization>> {
    const data = await httpPut<Organization>(`${BASE}/${id}/status`, { status, reason })
    return wrapResponse(data)
  },

  // ==================== Subscription (US4) ====================

  /** 单位套餐信息（映射自组织套餐字段） */
  async getOrgSubscription(id: string): Promise<ApiResponse<OrganizationSubscription>> {
    const data = await httpGet<OrganizationSubscription>(`${BASE}/${id}/subscription`)
    return wrapResponse(data)
  },

  /** 变更套餐 / 延期：planType 变更时按预设重置额度，planEndDate 同步订阅域到期时间 */
  async updateOrgSubscription(
    id: string,
    dto: { planType?: string; planEndDate: string; reason: string },
  ): Promise<ApiResponse<OrganizationSubscription>> {
    const data = await httpPut<OrganizationSubscription>(`${BASE}/${id}/subscription`, dto)
    return wrapResponse(data)
  },

  // ==================== Members (US5) ====================

  async listOrgMembers(
    id: string,
    params: { keyword?: string; page?: number; pageSize?: number } = {},
  ): Promise<ApiResponse<PaginatedResponse<OrgMember>>> {
    const query: Record<string, string | number> = {}
    if (params.keyword) query.keyword = params.keyword
    if (params.page) query.page = params.page
    if (params.pageSize) query.pageSize = params.pageSize
    const data = await httpGet<PaginatedResponse<OrgMember>>(`${BASE}/${id}/members`, { params: query })
    return wrapResponse(data)
  },

  /** 移除成员：成员迁回本人个人体验空间 */
  async removeOrgMember(id: string, userId: string): Promise<ApiResponse<void>> {
    await httpDelete(`${BASE}/${id}/members/${userId}`)
    return wrapResponse(undefined)
  },

  /** 重置成员密码：返回明文临时密码，成员下次登录须强制改密 */
  async resetOrgMemberPassword(
    id: string,
    userId: string,
  ): Promise<ApiResponse<{ tempPassword: string; forceChange: boolean }>> {
    const data = await httpPost<{ tempPassword: string; forceChange: boolean }>(
      `${BASE}/${id}/members/${userId}/reset-password`,
    )
    return wrapResponse(data)
  },

  // ==================== Stats ====================

  async getOrganizationStats(): Promise<ApiResponse<OrgStats>> {
    const data = await httpGet<OrgStats>(`${BASE}/stats`)
    return wrapResponse(data)
  },

  // ==================== Import / Export ====================

  async batchImportOrganizations(file: File, strategy: 'skip' | 'overwrite' | 'abort'): Promise<ApiResponse<BatchImportResult>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('strategy', strategy)
    const data = await httpPost<BatchImportResult>(`${BASE}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return wrapResponse(data)
  },

  async exportOrganizations(params: OrgListParams): Promise<Blob> {
    const query: Record<string, string | number> = {}
    if (params.keyword) query.keyword = params.keyword
    if (params.type) query.type = params.type
    if (params.status) query.status = params.status
    if (params.region) query.region = params.region
    if (params.planType) query.planType = params.planType

    const res = await httpClient.get(`${BASE}/export`, { params: query, responseType: 'blob' })
    return res.data as Blob
  },

  // ==================== Current User Organization ====================

  async getCurrentOrganization(): Promise<ApiResponse<Organization>> {
    const data = await httpGet<Organization>('/api/organizations/current')
    return wrapResponse(data)
  },

  async createPersonalSpace(): Promise<ApiResponse<Organization>> {
    const data = await httpPost<Organization>('/api/organizations/personal-space')
    return wrapResponse(data)
  },

  /** 个人体验空间转化为正式单位（升级套餐前置流程），成功后需刷新用户信息 */
  async convertOrganization(dto: ConvertOrgDto): Promise<ApiResponse<ConvertOrgResult>> {
    const data = await httpPost<ConvertOrgResult>('/api/organizations/convert', dto)
    return wrapResponse(data)
  },
}
